<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="800px"
  >
    <template v-slot:activator="{ on }">
      <v-btn 
        icon 
        x-small
        v-on="on"
      >
        <v-icon color="primary">
          mdi-pencil
        </v-icon>
      </v-btn>
    </template>
    
    <material-card
      :offset="10"
      :full-width="true"
      color="hrissecondary"
      title="Cost Center"
      text="Update Cost Center"
      class="mb-0"
      :loading="loading"
    >
      <form v-if="dialog && !loading">
        <v-container>
          <v-row>
            <v-col cols="6">
              <v-row no-gutters>
                <v-col cols="12">
                  <v-text-field
                    v-model="localCostCenter.name"
                    v-validate="'required'"
                    label="Cost Center Name"
                    :error-messages="errors.collect('name')"
                    data-vv-name="name"
                    data-vv-as="cost center name"
                    outlined
                  />
                </v-col>
              </v-row>

              <v-row no-gutters>
                <v-col cols="12">
                  <div>Is belongs to <span class="font-weight-bold primary--text">{{ selectedCompanyName }}</span> ?</div>

                  <v-radio-group
                    v-model="isActualCompany"     
                    row
                    @change="setActualCompanyValue(isActualCompany)"
                  >
                    <v-radio
                      label="Yes"
                      :value="1"
                    /> 
                    <v-radio
                      label="No"
                      :value="0"
                    />
                  </v-radio-group>
                </v-col>
              </v-row>

              <v-row 
                v-if="!isActualCompany" 
                no-gutters
              >
                <v-col cols="12">
                  <v-autocomplete
                    v-model="localCostCenter.actual_company_uid"
                    v-validate="'required'"
                    :items="companies"
                    item-text="name"
                    item-value="id"
                    value="id"
                    :error-messages="errors.collect('actual_company_uid')"
                    data-vv-name="actual_company_uid"
                    data-vv-as="actual company"
                    label="Actual Company"
                    outlined
                  />
                </v-col>
              </v-row>
            </v-col>
            
            <v-col cols="6">
              <v-row no-gutters>
                <v-col cols="12">
                  <v-text-field
                    v-model="localCostCenter.address_line_one"
                    v-validate="localCostCenter.name?'required':''"
                    :error-messages="errors.collect('address_line_one')"
                    data-vv-name="address_line_one"
                    data-vv-as="address"
                    :disabled="!localCostCenter.name"
                    :filled="!localCostCenter.name"
                    label="Address Line 1"
                    outlined
                  />
                </v-col>

                <v-col cols="12">
                  <v-text-field
                    v-model="localCostCenter.address_line_two"
                    :disabled="!localCostCenter.address_line_one"
                    outlined
                    :filled="!localCostCenter.address_line_one"
                    label="Address Line 2"
                  />
                </v-col>

                <v-col cols="12">
                  <v-text-field
                    v-model="localCostCenter.address_line_three"
                    :disabled="!localCostCenter.address_line_one"
                    outlined
                    :filled="!localCostCenter.address_line_one"
                    label="Address Line 3"
                  />
                </v-col>

                <v-col cols="12">
                  <v-autocomplete
                    v-model="localCostCenter.postcode"
                    v-validate="'required'"
                    :items="postcodes"
                    item-text="postcode"
                    item-value="postcode"
                    value="postcode"
                    :error-messages="errors.collect('postcode')"
                    data-vv-name="postcode"
                    data-vv-as="postcode"
                    outlined
                    label="Postcode"
                    @input="clearData(); postCodeOnChanged(localCostCenter.postcode)"
                  />
                </v-col>

                <v-col cols="12">
                  <v-autocomplete
                    v-model="localCostCenter.city_id"
                    v-validate="localCostCenter.name?'required':''"
                    :disabled="dropdownDisabled"
                    :filled="!localCostCenter.postcode"
                    :items="filteredCities"
                    :error-messages="errors.collect('city_id')"
                    data-vv-name="city_id"
                    data-vv-as="city"
                    item-text="name"
                    item-value="id"
                    value="id"
                    outlined
                    label="City"
                    hide-selected
                    @input="cityOnChanged(localCostCenter.city_id)"
                  />
                </v-col>

                <v-col cols="12">
                  <v-autocomplete
                    v-model="localCostCenter.state_id"
                    v-validate="localCostCenter.name?'required':''"
                    :error-messages="errors.collect('state_id')"
                    data-vv-name="state_id"
                    data-vv-as="state"
                    disabled
                    :filled="!localCostCenter.city_id"
                    :items="states"
                    item-text="name"
                    item-value="id"
                    value="id"
                    label="State"
                    outlined
                    hide-selected
                  />
                </v-col>

                <v-col cols="12">
                  <v-autocomplete
                    v-model="localCostCenter.country_id"
                    v-validate="localCostCenter.name?'required':''"
                    :error-messages="errors.collect('country_id')"
                    data-vv-name="country_id"
                    data-vv-as="country"
                    :items="countries"
                    disabled
                    :filled="!localCostCenter.state_id"
                    item-text="name"
                    item-value="id"
                    value="id"
                    outlined
                    label="Country"
                    hide-selected
                  />
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-container>
      </form> 

      <v-card-actions v-if="dialog && !loading">
        <v-spacer />
        <v-btn
          :disabled="progressing"
          text
          class="px-10"
          @click="dialog = false"
        >
          Cancel
        </v-btn>
        <v-btn
          :loading="progressing"
          color="hrissecondary"    
          class="white--text px-10"      
          @click="submitForm"
        >
          Update
        </v-btn>
      </v-card-actions> 
    </material-card>  
  </v-dialog>
</template>

<script>
  export default {
    $_veeValidate: {
      validator: 'new', 
    },
    props: {
      costCenter: {
        type: Object,
        default: null,
      },
      selectedCompanyName: {
        type: String,
        required: true,
      },
    },
    data () {
      return {
        dialog: false,
        progressing: false,
        localCostCenter: null,
        loading: false,
        countries: [],
        states: [],
        postcodes: [],
        cities: [],
        companies: [],
        isActualCompany: this.costCenter.selected_company_uid === this.costCenter.actual_company_uid ? 1 : 0,
        dropdownDisabled: null,
        cityIds: [],          
      }
    },
    computed: {
      filteredCities () {
        if (this.localCostCenter.postcode) {
          this.postCodeOnChanged(this.localCostCenter.postcode)
        }
        
        if (this.cityIds.length > 0) {
          return this.cities.filter(city => {
            return this.cityIds.includes(city.id)
          })
        }
        return this.cities      
      },          
    },    
    watch: {
      dialog: function (value) {
        if (!value) return 

        this.loading = true

        this.localCostCenter = { ...this.costCenter }

        Promise.all([
          this.$api.countries().list(),
          this.$api.states().list(),
          this.$api.cities().list(),
          this.$api.postcodes().list(),
          this.$api.hrisCompanies().list(),
        ])
          .then(([countries, states, cities, postcodes, companies]) => {
            this.countries = countries.data
            this.states = states.data
            this.cities = cities.data
            this.postcodes = _.orderBy(postcodes.data, ['postcode'], ['asc'])
            this.companies = companies.data.filter(company => {
              return company.id !== this.costCenter.selected_company_uid
            })
            this.loading = false
          })
      },
    },
    methods: { 
      setActualCompanyValue (isActualCompany) {
        if (!isActualCompany) this.localCostCenter.actual_company_uid = this.localCostCenter.actual_company_uid
        else this.localCostCenter.actual_company_uid = this.costCenter.selected_company_uid
      },
      submitForm () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return

            this.progressing = true
            
            this.$api.costCenters().update(this.costCenter.id, this.prepareFormData())
              .then(response => {
                if (response.status === 200) {
                  this.$emit('updated')
                  this.$snack({ message: 'Cost Center updated' })

                  this.progressing = false
                  this.dialog = false
                }
              })
          })
      },
      
      prepareFormData () {
        return {
          name: this.localCostCenter.name,
          selected_company_uid: this.localCostCenter.selected_company_uid,
          actual_company_uid: this.localCostCenter.actual_company_uid,
          address_line_one: this.localCostCenter.address_line_one,
          address_line_two: this.localCostCenter.address_line_two,
          address_line_three: this.localCostCenter.address_line_three,
          postcode: this.localCostCenter.postcode,
          city_id: this.localCostCenter.city_id,
          state_id: this.localCostCenter.state_id,
          country_id: this.localCostCenter.country_id,
        }
      },
      clearData () {
        this.localCostCenter.city_id = null
        this.localCostCenter.state_id = null
        this.localCostCenter.country_id = null
      },
      postCodeOnChanged (postcodeValue) {
        var cityID = this.postcodes.filter(p => {
          return p.postcode === postcodeValue
        })  

        this.cityIds = this.cities.filter(city => {
          return cityID.some(city2 => {
            return city2.city_id === city.id
          })
        }).map(city3 => {
          return city3.id
        })

        if (this.cityIds.length > 1) {
          this.dropdownDisabled = false
        } else {
          this.dropdownDisabled = true

          let cityId = (this.postcodes.find(postcode => {
            return postcode.postcode === postcodeValue
          }) || {}).city_id

          if (cityId) {
            var cityObject = this.getCity(cityId)
            if (cityObject) {
              this.localCostCenter.city_id = cityObject.id
              var stateObject = this.getState(cityObject.state_id)
              
              if (stateObject) {
                this.localCostCenter.state_id = stateObject.id
                var countryObject = this.getCountry(stateObject.country_id)
                this.localCostCenter.country_id = countryObject.id
              }
            }
          }
        }
      },
      cityOnChanged (cityId) {
        var cityObject = this.getCity(cityId)
        if (cityObject) {
          var stateObject = this.getState(cityObject.state_id)
          
          if (stateObject) {
            this.localCostCenter.state_id = stateObject.id
            var countryObject = this.getCountry(stateObject.country_id)
            this.localCostCenter.country_id = countryObject.id
          }
        }
      },      
      getCity (cityId) {
        if (isNaN(parseInt(cityId))) return null
        return this.cities.find(city => {
          return city.id === parseInt(cityId)
        })
      },
      getState (stateId) {
        if (isNaN(parseInt(stateId))) return null
        return this.states.find(state => {
          return state.id === parseInt(stateId)
        })
      },
      getCountry (countryId) {
        if (isNaN(parseInt(countryId))) return null
        return this.countries.find(country => {
          return country.id === parseInt(countryId)
        })
      },
  
    },
  }
</script>
